import * as React from "react";

/**
 * useReducedMotion — observes the `prefers-reduced-motion: reduce`
 * media query so JS-driven animations can opt out.
 *
 * Most of the platform handles `prefers-reduced-motion` via CSS
 * (a global rule in src/index.css collapses CSS animations to
 * 0.01ms when the user prefers reduced motion). JS-driven motion
 * (setInterval/setTimeout/requestAnimationFrame loops) escapes
 * that rule and needs an explicit opt-out — that's what this hook
 * is for.
 *
 * Returns `false` on the server / first paint (SSR-safe default —
 * playing the animation is the visual baseline), flips to `true`
 * on hydration when the OS-level preference is set. Re-renders
 * when the user toggles the preference live (rare but cheap to
 * support).
 *
 * Usage:
 *   const reducedMotion = useReducedMotion();
 *   useEffect(() => {
 *     if (reducedMotion) return; // skip the animation
 *     const id = setInterval(tick, 100);
 *     return () => clearInterval(id);
 *   }, [reducedMotion]);
 */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return reduced;
}
